// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// WB-002 — the on-the-wire op format shared by every whiteboard transport: the
// 1:1/mesh data channels (WB-003/004), the SFU `m.whiteboard` ephemeral frame
// (WB-005), and the saved snapshot (WB-013). A single versioned schema keeps the
// transports from drifting apart and survives federation.
//
// Wire format (v1): compact JSON, one envelope per op —
//
//     { "v": 1, "op": "stroke", "id": "...", "author": "...", "ts": 0, ... }
//
// `v` is the protocol version; a decoder drops any payload whose MAJOR version it
// doesn't understand rather than throwing. JSON is chosen for v1 for
// debuggability; payloads stay small because the engine already coalesces pointer
// samples into one stroke op, so a binary encoding can be slotted in behind the
// same `encode`/`decode` later without touching callers.
//
// SECURITY — author is NEVER trusted from the payload. `encode` writes whatever
// the sender stamps, but the RECEIVING transport layer overwrites `author` with
// the authenticated peer identity (data-channel peer id / WS `user_id`), mirroring
// the `handleSignal` anti-spoof rule (`internal/server/ephemeral.go`). `decode`
// therefore performs NO trust decision — it only validates structure.

import {
  type WireOp,
  type Op,
  type Point,
  type StrokeOp,
  type ShapeOp,
  type TextOp,
  type StickyOp,
  type EraseOp,
  type ClearOp,
  type BatchOp,
  type EnableAnnotationOp,
  type RestrictDrawingOp,
  type SnapshotRequestOp,
  type SnapshotOp,
  isDrawOp,
} from "./types";

/** Current wire protocol version. Bump the integer part on a breaking change. */
export const PROTOCOL_VERSION = 1;

/** The largest MAJOR version this build can decode. */
const SUPPORTED_MAJOR = Math.floor(PROTOCOL_VERSION);

// ── board_id helpers ─────────────────────────────────────────────────────────
// A board_id scopes ops to the right surface so two boards in the same call don't
// cross-contaminate. It rides the transport envelope (added in WB-003/005), not
// each op. Mode B is the room board; Mode A is per-presenter screen board.

/** Mode B (shared whiteboard): the room is the board. */
export function roomBoardId(roomID: string): string {
  return roomID;
}

/** Mode A (screen annotation): one board per presenter's share. */
export function screenBoardId(roomID: string, presenterID: string): string {
  return `${roomID}:screen:${presenterID}`;
}

// ── encode / decode ──────────────────────────────────────────────────────────

/** Serialize an op to its compact JSON wire form. */
export function encode(op: WireOp): string {
  return JSON.stringify({ v: PROTOCOL_VERSION, ...op });
}

/**
 * Parse a wire payload back into an op. Returns `null` — never throws — for
 * malformed JSON, an unknown/again-future MAJOR version, or a structurally
 * invalid op, so a transport can simply drop it.
 */
export function decode(data: string): WireOp | null {
  let raw: unknown;
  try {
    raw = JSON.parse(data);
  } catch {
    return null;
  }
  if (!isObject(raw)) return null;
  const v = (raw as { v?: unknown }).v;
  if (typeof v !== "number" || Math.floor(v) > SUPPORTED_MAJOR) return null;
  return validateOp(raw);
}

// ── validation ───────────────────────────────────────────────────────────────

function validateOp(raw: unknown): WireOp | null {
  if (!isObject(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (
    typeof r.op !== "string" ||
    typeof r.id !== "string" ||
    typeof r.author !== "string" ||
    typeof r.ts !== "number"
  ) {
    return null;
  }
  const base = {
    id: r.id,
    author: r.author,
    ts: r.ts,
    ...(typeof r.board_id === "string" ? { board_id: r.board_id } : {}),
  };
  switch (r.op) {
    case "stroke": {
      const points = validatePoints(r.points);
      if (!points || typeof r.color !== "string") return null;
      return {
        op: "stroke",
        ...base,
        color: r.color,
        points,
        ...(typeof r.highlight === "boolean" ? { highlight: r.highlight } : {}),
      } satisfies StrokeOp;
    }
    case "shape": {
      const from = validatePoint(r.from);
      const to = validatePoint(r.to);
      if (!from || !to || typeof r.color !== "string") return null;
      if (r.kind !== "line" && r.kind !== "rect" && r.kind !== "ellipse" && r.kind !== "arrow") return null;
      return { op: "shape", ...base, kind: r.kind, color: r.color, from, to } satisfies ShapeOp;
    }
    case "text": {
      const at = validatePoint(r.at);
      if (!at || typeof r.color !== "string" || typeof r.text !== "string") return null;
      return {
        op: "text",
        ...base,
        color: r.color,
        at,
        text: r.text,
        ...(typeof r.size === "number" ? { size: r.size } : {}),
      } satisfies TextOp;
    }
    case "sticky": {
      const at = validatePoint(r.at);
      if (!at || typeof r.color !== "string" || typeof r.text !== "string") return null;
      return { op: "sticky", ...base, color: r.color, at, text: r.text } satisfies StickyOp;
    }
    case "erase": {
      if (!isStringArray(r.targets)) return null;
      return { op: "erase", ...base, targets: r.targets } satisfies EraseOp;
    }
    case "clear":
      return { op: "clear", ...base } satisfies ClearOp;
    case "batch": {
      const ops = validateOpArray(r.ops);
      if (!ops) return null;
      return { op: "batch", ...base, ops } satisfies BatchOp;
    }
    case "enable_annotation": {
      if (typeof r.enabled !== "boolean") return null;
      return { op: "enable_annotation", ...base, enabled: r.enabled } satisfies EnableAnnotationOp;
    }
    case "restrict_drawing": {
      if (typeof r.min_power !== "number") return null;
      return {
        op: "restrict_drawing",
        ...base,
        min_power: r.min_power,
      } satisfies RestrictDrawingOp;
    }
    case "snapshot_request":
      return { op: "snapshot_request", ...base } satisfies SnapshotRequestOp;
    case "snapshot": {
      const ops = validateOpArray(r.ops);
      if (!ops) return null;
      return { op: "snapshot", ...base, ops } satisfies SnapshotOp;
    }
    default:
      return null; // unknown op kind — drop gracefully
  }
}

/** Validate an array of drawing ops (batch/snapshot payloads). */
function validateOpArray(raw: unknown): Op[] | null {
  if (!Array.isArray(raw)) return null;
  const out: Op[] = [];
  for (const item of raw) {
    const op = validateOp(item);
    // Nested payloads carry only draw + erase/clear ops, never control/batch/snapshot.
    if (!op || !(isDrawOp(op) || op.op === "erase" || op.op === "clear")) {
      return null;
    }
    out.push(op);
  }
  return out;
}

function validatePoint(raw: unknown): Point | null {
  if (!isObject(raw)) return null;
  const p = raw;
  if (typeof p.x !== "number" || typeof p.y !== "number") return null;
  return { x: p.x, y: p.y, ...(typeof p.w === "number" ? { w: p.w } : {}) };
}

function validatePoints(raw: unknown): Point[] | null {
  if (!Array.isArray(raw)) return null;
  const out: Point[] = [];
  for (const p of raw) {
    if (!isObject(p)) return null;
    const pt = p as Record<string, unknown>;
    if (typeof pt.x !== "number" || typeof pt.y !== "number") return null;
    out.push({
      x: pt.x,
      y: pt.y,
      ...(typeof pt.w === "number" ? { w: pt.w } : {}),
    });
  }
  return out;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === "string");
}
